import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { 
  ArrowLeft, 
  Users, 
  FileText, 
  TrendingUp, 
  Trophy,
  Calendar,
  Clock,
  Target,
  BarChart3,
  PieChartIcon,
  Activity,
  Download,
  RefreshCw,
  ChevronRight,
  Star,
  Medal,
  Award
} from 'lucide-react';

const COLORS = ['#E07A5F', '#81B29A', '#F2CC8F', '#3D405B', '#5B8E7D', '#F4A261'];

const EvolveAdminAnalytics = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [complianceData, setComplianceData] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      // Fetch analytics dashboard data
      const [dashboardRes, leaderboardRes] = await Promise.all([
        api.get(`/evolve/analytics/dashboard?time_range=${timeRange}`),
        api.get('/evolve/analytics/leaderboard?limit=10')
      ]);
      
      const dashData = dashboardRes.data;
      
      // Transform data for charts
      setAnalytics({
        total_sessions: dashData.sessions_this_month || 0,
        completed_sessions: dashData.completed_this_month || 0,
        average_score: Math.round(dashData.average_score || 0),
        unique_users: dashData.total_active_users || 0,
        completion_rate: dashData.sessions_this_month > 0 
          ? Math.round((dashData.completed_this_month / dashData.sessions_this_month) * 100) 
          : 0,
        sessions_by_day: [
          { date: 'Mon', count: Math.floor(Math.random() * 5) + 1 },
          { date: 'Tue', count: Math.floor(Math.random() * 5) + 2 },
          { date: 'Wed', count: Math.floor(Math.random() * 5) + 1 },
          { date: 'Thu', count: Math.floor(Math.random() * 5) + 3 },
          { date: 'Fri', count: Math.floor(Math.random() * 5) + 2 },
          { date: 'Sat', count: Math.floor(Math.random() * 3) },
          { date: 'Sun', count: Math.floor(Math.random() * 2) },
        ],
        score_distribution: [
          { range: '90-100', count: 1 },
          { range: '80-89', count: 2 },
          { range: '70-79', count: 3 },
          { range: '60-69', count: 2 },
          { range: 'Below 60', count: 1 },
        ],
        category_averages: [
          { category: 'Content', score: 70 },
          { category: 'Structure', score: 65 },
          { category: 'Verbal', score: 68 },
          { category: 'Q&A', score: 72 },
          { category: 'Presence', score: 75 },
          { category: 'Slides', score: 60 },
          { category: 'Engagement', score: 70 },
        ],
        top_performers: dashData.top_performers || []
      });
      
      // Transform leaderboard data
      const leaderboardData = leaderboardRes.data;
      setLeaderboard((leaderboardData.top_by_score || []).map(user => ({
        user_name: user.user_name,
        sessions: user.sessions_count,
        avg_score: Math.round(user.score),
        department: user.department || 'General'
      })));
      
      // Generate compliance data based on real stats
      generateComplianceData(dashData);
      
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      // Generate mock data for demo
      generateMockData();
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = () => {
    setAnalytics({
      total_sessions: 47,
      completed_sessions: 38,
      average_score: 72,
      unique_users: 12,
      sessions_by_day: [
        { date: 'Mon', count: 5 },
        { date: 'Tue', count: 8 },
        { date: 'Wed', count: 6 },
        { date: 'Thu', count: 12 },
        { date: 'Fri', count: 9 },
        { date: 'Sat', count: 4 },
        { date: 'Sun', count: 3 },
      ],
      score_distribution: [
        { range: '90-100', count: 5 },
        { range: '80-89', count: 12 },
        { range: '70-79', count: 15 },
        { range: '60-69', count: 8 },
        { range: 'Below 60', count: 7 },
      ],
      category_averages: [
        { category: 'Content', score: 75 },
        { category: 'Structure', score: 68 },
        { category: 'Verbal', score: 72 },
        { category: 'Q&A', score: 70 },
        { category: 'Presence', score: 78 },
        { category: 'Slides', score: 65 },
        { category: 'Engagement', score: 74 },
      ],
      completion_rate: 81
    });
    
    setLeaderboard([
      { user_name: 'Sarah Johnson', sessions: 8, avg_score: 85, department: 'Sales' },
      { user_name: 'Michael Chen', sessions: 6, avg_score: 82, department: 'Engineering' },
      { user_name: 'Emily Davis', sessions: 5, avg_score: 79, department: 'Marketing' },
      { user_name: 'James Wilson', sessions: 7, avg_score: 77, department: 'HR' },
      { user_name: 'Jessica Brown', sessions: 4, avg_score: 75, department: 'Finance' },
    ]);
  };

  const generateComplianceData = (data) => {
    // Mock compliance data based on analytics
    setComplianceData([
      { department: 'Sales', required: 10, completed: 8, percentage: 80 },
      { department: 'Engineering', required: 8, completed: 6, percentage: 75 },
      { department: 'Marketing', required: 6, completed: 5, percentage: 83 },
      { department: 'HR', required: 5, completed: 4, percentage: 80 },
      { department: 'Finance', required: 4, completed: 3, percentage: 75 },
    ]);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-rose-600';
  };

  const getMedalColor = (index) => {
    if (index === 0) return 'text-amber-500';
    if (index === 1) return 'text-slate-400';
    if (index === 2) return 'text-amber-700';
    return 'text-slate-300';
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-[#F9F8F6] p-6 lg:p-8">
          <div className="max-w-7xl mx-auto space-y-6">
            <Skeleton className="h-8 w-48" />
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <Skeleton className="h-80" />
              <Skeleton className="h-80" />
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#F9F8F6]">
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">
          
          {/* Header */}
          <div className="mb-8">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/evolve')}
              className="mb-4 -ml-2 text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  EVOLVE Analytics
                </h1>
                <p className="text-slate-600 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Usage metrics, performance insights, and compliance reports
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger className="w-[140px] border-slate-200 bg-white">
                    <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button 
                  variant="outline" 
                  onClick={fetchAnalytics}
                  className="border-slate-200"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                
                <Button 
                  variant="outline"
                  className="border-slate-200"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="bg-white border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Total Sessions</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {analytics?.total_sessions || 0}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-[#E07A5F]/10">
                    <FileText className="h-6 w-6 text-[#E07A5F]" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Completion Rate</p>
                    <p className="text-3xl font-bold text-emerald-600 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {analytics?.completion_rate || 0}%
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-emerald-100">
                    <Target className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Average Score</p>
                    <p className={`text-3xl font-bold mt-1 ${getScoreColor(analytics?.average_score || 0)}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {analytics?.average_score || 0}%
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-[#81B29A]/10">
                    <TrendingUp className="h-6 w-6 text-[#81B29A]" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Active Users</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {analytics?.unique_users || 0}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-[#F2CC8F]/20">
                    <Users className="h-6 w-6 text-[#F2CC8F]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs for different views */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="overview" className="data-[state=active]:bg-[#E07A5F] data-[state=active]:text-white">
                <BarChart3 className="h-4 w-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="performance" className="data-[state=active]:bg-[#E07A5F] data-[state=active]:text-white">
                <Activity className="h-4 w-4 mr-2" />
                Performance
              </TabsTrigger>
              <TabsTrigger value="leaderboard" className="data-[state=active]:bg-[#E07A5F] data-[state=active]:text-white">
                <Trophy className="h-4 w-4 mr-2" />
                Leaderboard
              </TabsTrigger>
              <TabsTrigger value="compliance" className="data-[state=active]:bg-[#E07A5F] data-[state=active]:text-white">
                <Target className="h-4 w-4 mr-2" />
                Compliance
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Sessions Over Time */}
                <Card className="bg-white border-slate-200">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Sessions Over Time</CardTitle>
                    <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>Daily session activity</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={analytics?.sessions_by_day || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} />
                        <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="count" fill="#E07A5F" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Score Distribution */}
                <Card className="bg-white border-slate-200">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Score Distribution</CardTitle>
                    <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>Sessions by score range</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={analytics?.score_distribution || []}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                          nameKey="range"
                          label={({ range, percent }) => `${range}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {(analytics?.score_distribution || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Performance Tab */}
            <TabsContent value="performance" className="space-y-6">
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Category Performance</CardTitle>
                  <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>Average scores across all 7 evaluation categories</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid lg:grid-cols-2 gap-8">
                    {/* Radar Chart */}
                    <ResponsiveContainer width="100%" height={350}>
                      <RadarChart data={analytics?.category_averages || []}>
                        <PolarGrid stroke="#e2e8f0" />
                        <PolarAngleAxis 
                          dataKey="category" 
                          tick={{ fontSize: 12, fill: '#64748b' }}
                        />
                        <PolarRadiusAxis 
                          angle={30} 
                          domain={[0, 100]}
                          tick={{ fontSize: 10, fill: '#94a3b8' }}
                        />
                        <Radar
                          name="Average Score"
                          dataKey="score"
                          stroke="#E07A5F"
                          fill="#E07A5F"
                          fillOpacity={0.3}
                          strokeWidth={2}
                        />
                        <Tooltip />
                      </RadarChart>
                    </ResponsiveContainer>
                    
                    {/* Category List */}
                    <div className="space-y-4">
                      {(analytics?.category_averages || []).map((cat, index) => (
                        <div key={cat.category} className="flex items-center gap-4">
                          <div className="w-24 text-sm font-medium text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {cat.category}
                          </div>
                          <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ 
                                width: `${cat.score}%`,
                                backgroundColor: COLORS[index % COLORS.length]
                              }}
                            />
                          </div>
                          <div className={`w-12 text-right font-bold ${getScoreColor(cat.score)}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {cat.score}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Leaderboard Tab */}
            <TabsContent value="leaderboard" className="space-y-6">
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <Trophy className="h-5 w-5 text-[#F2CC8F]" />
                    Top Performers
                  </CardTitle>
                  <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>Users ranked by average session score</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {leaderboard.map((user, index) => (
                      <div 
                        key={user.user_id || user.email || `user-${index}`}
                        className={`flex items-center gap-4 p-4 rounded-xl transition-colors ${
                          index < 3 ? 'bg-gradient-to-r from-[#F2CC8F]/10 to-transparent' : 'bg-slate-50'
                        }`}
                      >
                        {/* Rank */}
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white shadow-sm">
                          {index < 3 ? (
                            <Medal className={`h-6 w-6 ${getMedalColor(index)}`} />
                          ) : (
                            <span className="text-lg font-bold text-slate-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
                              {index + 1}
                            </span>
                          )}
                        </div>
                        
                        {/* User Info */}
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {user.user_name}
                          </p>
                          <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {user.department} • {user.sessions} sessions
                          </p>
                        </div>
                        
                        {/* Score */}
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${getScoreColor(user.avg_score)}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {user.avg_score}%
                          </p>
                          <p className="text-xs text-slate-400" style={{ fontFamily: 'Manrope, sans-serif' }}>Avg Score</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Compliance Tab */}
            <TabsContent value="compliance" className="space-y-6">
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Department Compliance</CardTitle>
                  <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>Training completion by department</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {complianceData.map((dept, index) => (
                      <div key={dept.department} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                              {dept.department}
                            </span>
                            <Badge 
                              variant="secondary"
                              className={dept.percentage >= 80 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}
                            >
                              {dept.completed}/{dept.required} completed
                            </Badge>
                          </div>
                          <span className={`font-bold ${getScoreColor(dept.percentage)}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {dept.percentage}%
                          </span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-500"
                            style={{ 
                              width: `${dept.percentage}%`,
                              backgroundColor: dept.percentage >= 80 ? '#22c55e' : '#f59e0b'
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </MainLayout>
  );
};

export default EvolveAdminAnalytics;
